import React, { Component, ReactNode } from 'react';
import { errorRequest } from 'core/services/product';
import { store } from 'core/store/configureStore';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  retryCount: number;
  isRetrying: boolean;
}

export class ChunkErrorBoundary extends Component<Props, State> {
  private maxRetries = 3;
  private retryTimeouts: NodeJS.Timeout[] = [];

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      retryCount: 0,
      isRetrying: false,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Check if this is a chunk loading error
    const isChunkError =
      error?.name === 'ChunkLoadError' ||
      error?.message?.includes('Loading chunk') ||
      error?.message?.includes('timeout') ||
      error?.message?.includes('failed');

    return {
      hasError: true,
      error: isChunkError ? error : null,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const isChunkError =
      error?.name === 'ChunkLoadError' ||
      error?.message?.includes('Loading chunk') ||
      error?.message?.includes('timeout') ||
      error?.message?.includes('failed');

    if (isChunkError) {
      console.warn('Chunk loading error caught by boundary:', error);

      // Report error to backend
      store.dispatch(
        errorRequest({
          code: 2,
          message: `Chunk loading error: ${error.message}`,
          stackTrace: JSON.stringify(errorInfo),
        }),
      );

      // Auto-retry for chunk errors
      this.handleChunkErrorRetry();
    } else {
      // For non-chunk errors, just report and show fallback
      store.dispatch(
        errorRequest({
          code: 1,
          message: error.message,
          stackTrace: error.stack || 'unknown',
        }),
      );
    }
  }

  componentWillUnmount() {
    // Clear any pending timeouts
    this.retryTimeouts.forEach(timeout => clearTimeout(timeout));
  }

  handleChunkErrorRetry = () => {
    if (this.state.retryCount >= this.maxRetries) {
      console.error('Max chunk error retries exceeded, forcing page reload');
      this.handleForceReload();
      return;
    }

    this.setState({ isRetrying: true });

    // Clear webpack cache
    this.clearWebpackCache();

    // Progressive retry delays: 1s, 2s, 4s
    const retryDelay = Math.pow(2, this.state.retryCount) * 1000;

    const timeout = setTimeout(() => {
      this.setState(prevState => ({
        hasError: false,
        error: null,
        retryCount: prevState.retryCount + 1,
        isRetrying: false,
      }));
    }, retryDelay);

    this.retryTimeouts.push(timeout);
  };

  clearWebpackCache = () => {
    try {
      // Clear webpack module cache
      if (typeof window !== 'undefined' && (window as any).__webpack_require__) {
        const webpackRequire = (window as any).__webpack_require__;
        if (webpackRequire.cache) {
          Object.keys(webpackRequire.cache).forEach(key => {
            if (key.includes('chunk')) {
              delete webpackRequire.cache[key];
            }
          });
        }
      }

      // Clear browser cache for JS files
      if ('caches' in window) {
        caches.keys().then(cacheNames => {
          cacheNames.forEach(cacheName => {
            if (cacheName.includes('js') || cacheName.includes('chunk')) {
              caches.delete(cacheName);
            }
          });
        });
      }
    } catch (error) {
      console.warn('Failed to clear webpack cache:', error);
    }
  };

  handleForceReload = () => {
    // Clear all caches before reload
    if ('caches' in window) {
      caches.keys().then(cacheNames => {
        Promise.all(cacheNames.map(cacheName => caches.delete(cacheName))).then(() => {
          (window as any).location.reload();
        });
      });
    } else {
      // Fallback: add cache-busting parameter
      const url = new URL((window as any).location.href);
      url.searchParams.set('_cache_bust', Date.now().toString());
      (window as any).location.href = url.toString();
    }
  };

  handleManualRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      retryCount: 0,
      isRetrying: false,
    });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.state.isRetrying) {
        return (
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
              <p className="text-lg text-gray-700 dark:text-gray-300">Recovering from loading error...</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Retry {this.state.retryCount + 1} of {this.maxRetries}
              </p>
            </div>
          </div>
        );
      }

      // Show chunk error fallback
      return (
        this.props.fallback || (
          <div className="flex items-center justify-center min-h-screen">
            <div className="max-w-md text-center p-6 bg-white dark:bg-gray-800 rounded-lg shadow-lg">
              <div className="text-6xl mb-4">⚠️</div>
              <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-2">Loading Error</h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                Some application resources failed to load. This might be due to a network issue or outdated cache.
              </p>
              <div className="space-y-3">
                <button
                  onClick={this.handleManualRetry}
                  className="w-full px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                  disabled={this.state.retryCount >= this.maxRetries}
                >
                  Try Again
                </button>
                <button
                  onClick={this.handleForceReload}
                  className="w-full px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                >
                  Reload Page
                </button>
              </div>
              {this.state.retryCount > 0 && (
                <p className="text-xs text-gray-500 mt-4">
                  Retry attempts: {this.state.retryCount}/{this.maxRetries}
                </p>
              )}
            </div>
          </div>
        )
      );
    }

    return this.state.hasError ? null : this.props.children;
  }
}

// Higher-order component to wrap components with chunk error boundary
export function withChunkErrorBoundary<T extends Record<string, any>>(
  WrappedComponent: React.ComponentType<T>,
  fallback?: ReactNode,
) {
  return function ChunkErrorBoundaryWrapper(props: T) {
    return (
      <ChunkErrorBoundary fallback={fallback}>
        <WrappedComponent {...props} />
      </ChunkErrorBoundary>
    );
  };
}
